import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, Routes, useParams, Route, useResolvedPath } from 'react-router-dom';
import InfosecAssessment from './assessment';
import InfosecAuth from './auth';
import InfosecJoin from './join';
import InfosecFinish from './finish';
import InfosecError from './error';
import { joinPath, querystring } from '../../utils/helpers';
import AssessmentsAnswerView from '../Assessments/AssessmentsAnswerView';
import { useStores } from '../../hooks/use-stores';
import { observer } from 'mobx-react';
// eslint-disable-next-line no-unused-vars
import AssessmentResults from '../Assessments/AssessmentResults';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import { Box } from '@mui/material';

function InfosecHome({ variant = 'infosec' }) {
    const { miniHRAKey } = useParams();
    const { authStore, assessmentStore, questionStore, brandingStore } =
        useStores();
    const { miniHRAOptionsLoading, miniHRAOptions, error } = assessmentStore;
    const { isAuthenticated, currentUser } = authStore;
    const { brandingLoaded, emailOptions } = brandingStore;
    const [abEmailTestOption, setAbEmailTestOption] = useState();
    const [loadingError, setLoadingError] = useState(null);

    const resolved = useResolvedPath('.');
    const baseUrl = resolved.pathname;

    const effectiveVariant = useMemo(() => {
        if (miniHRAOptions) return miniHRAOptions.id;
        return variant;
    }, [miniHRAOptions, variant]);

    useEffect(() => {
        return () => {
            assessmentStore.resetMiniHRAOptionsLoading();
        };
    }, []);

    useEffect(() => {
        if (miniHRAKey) {
            assessmentStore.loadMiniHRAOptions(miniHRAKey);
        }
    }, [miniHRAKey]);

    useEffect(() => {
        if (!miniHRAKey) return;
        
        if (miniHRAOptionsLoading === false) {
            if (miniHRAOptions) {
                brandingStore.setCustomBranding(miniHRAKey);
            } else if (error) {
                setLoadingError(error);
                if (error.includes('not found')) {
                    brandingStore.loadBranding();
                } else {
                    brandingStore.setCustomBranding(miniHRAKey);
                }
            }
        }
    }, [miniHRAKey, miniHRAOptionsLoading, miniHRAOptions, error]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!brandingLoaded) return;
        let emailOption =
            (currentUser && currentUser.emailOption) ||
            emailOptions['default'] ||
            'required-email';
        if (emailOptions[variant]) {
            emailOption = emailOptions[variant];
        } else if (variant === 'ai-challenge') {
            emailOption = Math.random() > 0.5 ? 'no-email' : 'optional-email';
        }

        setAbEmailTestOption(emailOption);
    }, [emailOptions]);

    useEffect(() => {
        if (
            authStore.currentUser &&
            authStore.currentUser.variant !== effectiveVariant &&
            (!miniHRAKey ||
                (!miniHRAOptionsLoading && miniHRAOptionsLoading != null))
        ) {
            console.log('user needs to be logged out');
            authStore.forgetUser();
        }
    }, [effectiveVariant, miniHRAOptionsLoading, miniHRAKey]);


    if (
        miniHRAKey &&
        (miniHRAOptionsLoading || miniHRAOptionsLoading == null || !brandingLoaded)
    ) {
        return null;
    }

    if (miniHRAKey && loadingError) {
        return <InfosecError error={loadingError} />;
    }

    // Root component
    const RootComponent = () => {
        if (isAuthenticated)
            return <Navigate to="assessment" />;
        else
            return (
                <InfosecAuth
                    variant={effectiveVariant}
                    emailOption={
                        miniHRAOptions
                            ? miniHRAOptions.authentication
                            : abEmailTestOption
                    }
                    availableLanguages={
                        miniHRAOptions &&
                        miniHRAOptions.availableLanguages
                            ? miniHRAOptions.availableLanguages
                            : ['en', 'is']
                    }
                />
            );
    };

    // Assessment Answers View
    const AssessmentAnswersComponent = () => {
        let questionIndex = querystring('index');
        return (
            <AssessmentsAnswerView
                companyId={effectiveVariant}
                assessmentId={effectiveVariant}
                assessmentStore={assessmentStore}
                questionStore={questionStore}
                currentUser={currentUser}
                index={questionIndex}
                includeIdOnClose={false}
                parentUrl={baseUrl}
            />
        );
    };

    // Redirect to assessment answers
    const RedirectToAnswers = () => {
        let questionIndex = querystring('index');
        const query = questionIndex ? `?index=${questionIndex}` : '';

        console.log("Redirecting to answers with query:", baseUrl);
        return <Navigate to={ joinPath( baseUrl, `assessment/answers${query}`)} />;
    };

    return (
        <Routes>
            <Route index element={<RootComponent />} />
            <Route path="join" element={<InfosecJoin />} />

            {/* Protected routes - redirect to root if not authenticated */}
            {!isAuthenticated ? (
                <Route path="*" element={<Navigate to="" replace />} />
            ) : (
                <>
                    <Route
                        path="assessment/answers"
                        element={<AssessmentAnswersComponent />}
                    />
                    <Route
                        path="assessment/:id/answers"
                        element={<RedirectToAnswers />}
                    />
                    <Route path="finish" element={<InfosecFinish />} />
                    <Route
                        path="assessment/"
                        element={
                            <InfosecAssessment
                                availableLanguages={
                                    miniHRAOptions &&
                                    miniHRAOptions.availableLanguages
                                        ? miniHRAOptions.availableLanguages
                                        : ['en', 'is']
                                }
                                variant={effectiveVariant}
                                parentUrl={baseUrl}
                            />
                        }
                    />
                </>
            )}
        </Routes>
    );
}

export default observer(InfosecHome);
